import { useCallback } from 'react';
import { MapLocationData } from '@hero/web-components';

export interface UseMapConfirmationHandlersOptions {
  /** Current locations */
  locations: MapLocationData[];
  /** Initial locations */
  initialLocations: MapLocationData[];
  /** Current editing index */
  editingIndex: number | null;
  /** Callback when locations are ready */
  onLocationsReady?: (locations: MapLocationData[]) => void;
  /** Callback when changes are cancelled */
  onCancel?: () => void;
  /** Function to set locations */
  setLocations: (
    locations: MapLocationData[] | ((prev: MapLocationData[]) => MapLocationData[]),
  ) => void;
  /** Function to exit edit mode */
  exitEditMode: () => void;
  /** Function to get last confirmed locations */
  getLastConfirmedLocations: () => MapLocationData[];
  /** Function to update confirmed locations */
  updateConfirmedLocations: (locations: MapLocationData[]) => void;
}

/**
 * Hook for save/cancel confirmation handlers
 * Handles saving changes and canceling/reverting changes
 */
export function useMapConfirmationHandlers({
  locations,
  initialLocations,
  editingIndex,
  onLocationsReady,
  onCancel,
  setLocations,
  exitEditMode,
  getLastConfirmedLocations,
  updateConfirmedLocations,
}: UseMapConfirmationHandlersOptions) {
  // Handle save changes button
  const handleSaveChanges = useCallback(() => {
    if (onLocationsReady) {
      onLocationsReady([...locations]);
      updateConfirmedLocations(locations);
    }
  }, [locations, onLocationsReady, updateConfirmedLocations]);

  // Handle cancel changes button
  const handleCancel = useCallback(() => {
    const lastConfirmed = getLastConfirmedLocations();
    const resetLocations =
      lastConfirmed.length > 0
        ? lastConfirmed.map(loc => ({ ...loc }))
        : initialLocations.map(loc => ({ ...loc }));

    setLocations(resetLocations);

    if (editingIndex !== null) {
      exitEditMode();
    }

    // Notify parent that changes were cancelled
    if (onCancel) {
      onCancel();
    }
  }, [initialLocations, setLocations, editingIndex, exitEditMode, getLastConfirmedLocations, onCancel]);

  return {
    handleSaveChanges,
    handleCancel,
  };
}
